import type { FormEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Dismiss20Regular,
  Save20Regular,
  Delete20Regular,
  ArrowTrending20Regular,
  CheckmarkCircle20Regular,
  Mail20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { formatCurrency, formatDateShort } from "../Obras/obraFormat";
import {
  ApiError,
  deleteRentalContract,
  getRentalContractById,
  getRentalContractStatuses,
  updateRentalContract,
  type RentalContractPayload,
  type RentalContractStatus,
} from "../../arrendamentos/arrendamentosApi";
import {
  applyRentUpdateProposal,
  createRentUpdateProposal,
  deleteRentUpdateProposal,
  getRentUpdateProposals,
  notifyRentUpdateProposal,
  type RentUpdateProposal,
} from "../../arrendamentos/rentUpdatesApi";
import { getProperties, type Property } from "../../imoveis/imoveisApi";
import { getTenants, type Tenant } from "../../inquilinos/inquilinosApi";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "../../components/SearchableSelect";

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para editar contratos de arrendamento.";
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Não foi possível atualizar o contrato de arrendamento.";
};

const parseLoadError = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar contratos de arrendamento.";
    }
    if (error.status === 404) {
      return "Contrato de arrendamento não encontrado.";
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Não foi possível carregar o contrato de arrendamento.";
};

// Valor cru do <input type="date"> = porção YYYY-MM-DD da data ISO (sem conversão
// de fuso, para não deslocar o dia).
const toDateInput = (value?: string | null): string =>
  value ? value.slice(0, 10) : "";

// Estado da proposta de atualização [5.4.3] — classes já compiladas (grep-verificadas).
const PROPOSAL_STATUS_BADGE: Record<string, string> = {
  proposta: "bg-yellow-500/10 text-yellow-700",
  aplicada: "bg-green-500/10 text-green-700",
  rejeitada: "bg-muted text-secondary-text",
};

const PROPOSAL_STATUS_LABEL: Record<string, string> = {
  proposta: "Proposta",
  aplicada: "Aplicada",
  rejeitada: "Rejeitada",
};

const proposalStatusBadgeClass = (status: string): string =>
  PROPOSAL_STATUS_BADGE[status] ?? "bg-muted text-secondary-text";

const proposalStatusLabel = (status: string): string =>
  PROPOSAL_STATUS_LABEL[status] ?? status;

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

const inputClass =
  "w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary";
const inputStyle = {
  borderRadius: "var(--radius-button)",
  fontSize: "var(--text-base)",
} as const;
const labelClass = "block text-foreground mb-2";
const labelStyle = {
  fontSize: "var(--text-caption)",
  fontWeight: "var(--font-weight-semi-bold)",
} as const;

export function EditarArrendamentoView() {
  const navigate = useNavigate();
  const { contractId } = useParams();

  const [properties, setProperties] = useState<Property[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [statuses, setStatuses] = useState<RentalContractStatus[]>([]);

  const [contractSnapshotId, setContractSnapshotId] = useState<number | null>(null);
  const [propertyId, setPropertyId] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [contractNumber, setContractNumber] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [monthlyRent, setMonthlyRent] = useState("");
  const [paymentDueDay, setPaymentDueDay] = useState("1");
  const [deposit, setDeposit] = useState("");
  const [statusId, setStatusId] = useState("");
  const [notes, setNotes] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Atualização anual de renda [5.4.3] -------------------------------------
  // Renda mensal atualmente guardada no contrato (base da pré-visualização).
  const [contractRent, setContractRent] = useState<number>(0);
  const [proposals, setProposals] = useState<RentUpdateProposal[]>([]);
  const [isLoadingProposals, setIsLoadingProposals] = useState(false);
  const [coefficient, setCoefficient] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [proposalNotes, setProposalNotes] = useState("");
  const [isCreatingProposal, setIsCreatingProposal] = useState(false);
  const [proposalError, setProposalError] = useState<string | null>(null);
  const [proposalBusyId, setProposalBusyId] = useState<number | null>(null);
  const [confirmApplyId, setConfirmApplyId] = useState<number | null>(null);
  const [confirmDeleteProposalId, setConfirmDeleteProposalId] = useState<
    number | null
  >(null);

  useEffect(() => {
    let isMounted = true;
    getProperties()
      .then((data) => isMounted && setProperties(data))
      .catch(() => isMounted && setProperties([]));
    getTenants()
      .then((data) => isMounted && setTenants(data))
      .catch(() => isMounted && setTenants([]));
    getRentalContractStatuses()
      .then((data) => isMounted && setStatuses(data))
      .catch(() => isMounted && setStatuses([]));
    return () => {
      isMounted = false;
    };
  }, []);

  const propertyOptions = useMemo<SearchableSelectOption<string>[]>(
    () => properties.map((p) => ({ value: String(p.id), label: p.name })),
    [properties],
  );

  const tenantOptions = useMemo<SearchableSelectOption<string>[]>(
    () => tenants.map((t) => ({ value: String(t.id), label: t.name })),
    [tenants],
  );

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!contractId) {
        setLoadError("Contrato inválido.");
        setIsLoading(false);
        return;
      }
      const parsedId = Number(contractId);
      if (Number.isNaN(parsedId)) {
        setLoadError("Contrato inválido.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError(null);
      setSubmitError(null);

      try {
        const c = await getRentalContractById(parsedId);
        if (!isMounted) return;
        setContractSnapshotId(c.id);
        setPropertyId(String(c.propertyId));
        setTenantId(String(c.tenantId));
        setContractNumber(c.contractNumber ?? "");
        setStartDate(toDateInput(c.startDate));
        setEndDate(toDateInput(c.endDate));
        setMonthlyRent(String(c.monthlyRent));
        setContractRent(c.monthlyRent);
        setPaymentDueDay(String(c.paymentDueDay));
        setDeposit(
          c.deposit !== null && c.deposit !== undefined ? String(c.deposit) : "",
        );
        setStatusId(String(c.statusId));
        setNotes(c.notes ?? "");
      } catch (error) {
        if (!isMounted) return;
        setLoadError(parseLoadError(error));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [contractId]);

  // Atualização anual de renda [5.4.3] — carregar/gerir propostas -----------
  const loadProposals = useCallback(async (id: number) => {
    setIsLoadingProposals(true);
    setProposalError(null);
    try {
      setProposals(await getRentUpdateProposals(id));
    } catch (err) {
      setProposalError(
        errorMessage(err, "Não foi possível carregar as propostas de atualização."),
      );
    } finally {
      setIsLoadingProposals(false);
    }
  }, []);

  useEffect(() => {
    const parsedId = Number(contractId);
    if (!contractId || Number.isNaN(parsedId)) return;
    loadProposals(parsedId);
  }, [contractId, loadProposals]);

  const parsedCoefficient =
    coefficient.trim() !== "" ? parseFloat(coefficient.replace(",", ".")) : NaN;
  const hasValidCoefficient = Number.isFinite(parsedCoefficient);
  const previewRent = hasValidCoefficient
    ? contractRent * (1 + parsedCoefficient / 100)
    : null;

  const handleCreateProposal = async () => {
    if (!contractSnapshotId || isCreatingProposal) return;
    if (!hasValidCoefficient) {
      setProposalError("Indique um coeficiente válido.");
      return;
    }
    if (!effectiveDate) {
      setProposalError("Indique a data de efeito.");
      return;
    }
    setIsCreatingProposal(true);
    setProposalError(null);
    try {
      await createRentUpdateProposal({
        rentalContractId: contractSnapshotId,
        effectiveDate,
        coefficientPercent: parsedCoefficient,
        notes: proposalNotes.trim() || null,
      });
      setCoefficient("");
      setEffectiveDate("");
      setProposalNotes("");
      await loadProposals(contractSnapshotId);
    } catch (err) {
      setProposalError(errorMessage(err, "Não foi possível criar a proposta."));
    } finally {
      setIsCreatingProposal(false);
    }
  };

  const handleApplyProposal = async (proposal: RentUpdateProposal) => {
    if (proposalBusyId !== null) return;
    setProposalBusyId(proposal.id);
    setProposalError(null);
    try {
      const applied = await applyRentUpdateProposal(proposal.id);
      setConfirmApplyId(null);
      // A renda do contrato mudou — reflete no formulário e na pré-visualização.
      setMonthlyRent(String(applied.proposedRent));
      setContractRent(applied.proposedRent);
      if (contractSnapshotId) await loadProposals(contractSnapshotId);
    } catch (err) {
      setProposalError(errorMessage(err, "Não foi possível aplicar a proposta."));
    } finally {
      setProposalBusyId(null);
    }
  };

  const handleNotifyProposal = async (proposal: RentUpdateProposal) => {
    if (proposalBusyId !== null) return;
    setProposalBusyId(proposal.id);
    setProposalError(null);
    try {
      await notifyRentUpdateProposal(proposal.id);
      if (contractSnapshotId) await loadProposals(contractSnapshotId);
    } catch (err) {
      setProposalError(errorMessage(err, "Não foi possível notificar o inquilino."));
    } finally {
      setProposalBusyId(null);
    }
  };

  const handleDeleteProposal = async (proposal: RentUpdateProposal) => {
    if (proposalBusyId !== null) return;
    setProposalBusyId(proposal.id);
    setProposalError(null);
    try {
      await deleteRentUpdateProposal(proposal.id);
      setConfirmDeleteProposalId(null);
      setProposals((prev) => prev.filter((p) => p.id !== proposal.id));
    } catch (err) {
      setProposalError(errorMessage(err, "Não foi possível apagar a proposta."));
    } finally {
      setProposalBusyId(null);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!contractSnapshotId) {
      setSubmitError("Contrato inválido.");
      return;
    }
    if (!propertyId) {
      setSubmitError("Selecione o imóvel.");
      return;
    }
    if (!tenantId) {
      setSubmitError("Selecione o inquilino.");
      return;
    }
    if (!startDate) {
      setSubmitError("A data de início é obrigatória.");
      return;
    }
    const rent = monthlyRent !== "" ? parseFloat(monthlyRent) : NaN;
    if (!Number.isFinite(rent)) {
      setSubmitError("Indique a renda mensal.");
      return;
    }
    const dueDay = paymentDueDay !== "" ? parseInt(paymentDueDay, 10) : NaN;
    if (!Number.isFinite(dueDay) || dueDay < 1 || dueDay > 31) {
      setSubmitError("O dia de pagamento deve estar entre 1 e 31.");
      return;
    }

    setIsSaving(true);

    const payload: RentalContractPayload = {
      propertyId: Number(propertyId),
      tenantId: Number(tenantId),
      contractNumber: contractNumber.trim() || null,
      startDate,
      endDate: endDate || null,
      monthlyRent: rent,
      paymentDueDay: dueDay,
      deposit: deposit !== "" ? parseFloat(deposit) : null,
      statusId: statusId ? Number(statusId) : null,
      notes: notes.trim() || null,
    };

    try {
      await updateRentalContract(contractSnapshotId, payload);
    } catch (error) {
      setSubmitError(parseErrorMessage(error));
      setIsSaving(false);
      return;
    }

    setIsSaving(false);
    navigate("/arrendamentos", {
      state: { toast: "Contrato de arrendamento atualizado com sucesso" },
    });
  };

  const handleDelete = async () => {
    if (!contractSnapshotId || isDeleting) return;
    setIsDeleting(true);
    setSubmitError(null);
    try {
      await deleteRentalContract(contractSnapshotId);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        // Já não existe — trata como apagado.
      } else {
        setSubmitError(parseErrorMessage(error));
        setIsDeleting(false);
        setConfirmingDelete(false);
        return;
      }
    }
    navigate("/arrendamentos", {
      state: { toast: "Contrato de arrendamento apagado" },
    });
  };

  const actions = (
    <div className="flex items-center gap-4">
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => navigate("/arrendamentos")}
        disabled={isSaving}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => {
          const form = document.getElementById(
            "editar-arrendamento-form",
          ) as HTMLFormElement | null;
          form?.requestSubmit();
        }}
        disabled={isSaving || isLoading}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A guardar..." : "Guardar Alterações"}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Editar Contrato de Arrendamento"
        subtitle="Atualize os dados do contrato."
        actions={actions}
        backButton={{ onClick: () => navigate("/arrendamentos") }}
      />

      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          {loadError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {loadError}
            </div>
          )}

          {submitError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {submitError}
            </div>
          )}

          <form id="editar-arrendamento-form" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Contrato
                  </h3>

                  <div>
                    <SearchableSelect<string>
                      id="propertyId"
                      label={
                        <>
                          Imóvel <span className="text-primary">*</span>
                        </>
                      }
                      labelClassName={labelClass}
                      labelStyle={labelStyle}
                      value={propertyId === "" ? null : propertyId}
                      onChange={(next) => setPropertyId(next ?? "")}
                      options={propertyOptions}
                      placeholder="Selecione o imóvel"
                      searchPlaceholder="Procurar imóvel…"
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <SearchableSelect<string>
                      id="tenantId"
                      label={
                        <>
                          Inquilino <span className="text-primary">*</span>
                        </>
                      }
                      labelClassName={labelClass}
                      labelStyle={labelStyle}
                      value={tenantId === "" ? null : tenantId}
                      onChange={(next) => setTenantId(next ?? "")}
                      options={tenantOptions}
                      placeholder="Selecione o inquilino"
                      searchPlaceholder="Procurar inquilino…"
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label htmlFor="contractNumber" className={labelClass} style={labelStyle}>
                      Nº de contrato
                    </label>
                    <input
                      id="contractNumber"
                      type="text"
                      placeholder="Opcional"
                      value={contractNumber}
                      onChange={(e) => setContractNumber(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label htmlFor="startDate" className={labelClass} style={labelStyle}>
                      Data de início <span className="text-primary">*</span>
                    </label>
                    <input
                      id="startDate"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label htmlFor="endDate" className={labelClass} style={labelStyle}>
                      Data de fim
                    </label>
                    <input
                      id="endDate"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                      disabled={isLoading}
                    />
                    <p
                      className="text-secondary-text mt-1"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Deixe vazio para contrato sem termo.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="statusId" className={labelClass} style={labelStyle}>
                      Estado
                    </label>
                    <select
                      id="statusId"
                      value={statusId}
                      onChange={(e) => setStatusId(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                      disabled={isLoading}
                    >
                      {statuses.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Condições Financeiras
                  </h3>

                  <div>
                    <label htmlFor="monthlyRent" className={labelClass} style={labelStyle}>
                      Renda mensal (€) <span className="text-primary">*</span>
                    </label>
                    <input
                      id="monthlyRent"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={monthlyRent}
                      onChange={(e) => setMonthlyRent(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label htmlFor="paymentDueDay" className={labelClass} style={labelStyle}>
                      Dia de pagamento
                    </label>
                    <input
                      id="paymentDueDay"
                      type="number"
                      min="1"
                      max="31"
                      step="1"
                      value={paymentDueDay}
                      onChange={(e) => setPaymentDueDay(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label htmlFor="deposit" className={labelClass} style={labelStyle}>
                      Caução (€)
                    </label>
                    <input
                      id="deposit"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={deposit}
                      onChange={(e) => setDeposit(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label htmlFor="notes" className={labelClass} style={labelStyle}>
                      Notas
                    </label>
                    <textarea
                      id="notes"
                      rows={4}
                      placeholder="Opcional"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div
                  className="bg-card border border-border p-6 space-y-4"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Apagar contrato
                  </h3>
                  <p
                    className="text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    Esta ação é permanente e não pode ser revertida.
                  </p>
                  {confirmingDelete ? (
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-sm text-foreground">
                        Confirmar eliminação?
                      </span>
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={isDeleting || isLoading}
                        className="px-4 py-2 min-h-[44px] bg-destructive/10 text-destructive cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ borderRadius: "var(--radius-button)" }}
                      >
                        {isDeleting ? "A apagar..." : "Sim, apagar"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingDelete(false)}
                        disabled={isDeleting}
                        className="px-4 py-2 min-h-[44px] border border-border cursor-pointer"
                        style={{ borderRadius: "var(--radius-button)" }}
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmingDelete(true)}
                      disabled={isLoading}
                      className="px-4 py-2 min-h-[44px] border border-border text-destructive cursor-pointer flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ borderRadius: "var(--radius-button)" }}
                    >
                      <Delete20Regular className="w-5 h-5" />
                      Apagar contrato
                    </button>
                  )}
                </div>
              </div>
            </div>
          </form>

          {/* [5.4.3] Atualização anual de renda */}
          <div
            className="bg-card border border-border p-6 space-y-5 mt-6"
            style={{ borderRadius: "var(--radius-card)" }}
          >
            <div className="flex items-center gap-2">
              <ArrowTrending20Regular className="w-5 h-5 text-primary" />
              <h3
                className="text-foreground"
                style={{
                  fontSize: "var(--text-section-title)",
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Atualização anual de renda
              </h3>
            </div>
            <p
              className="text-secondary-text"
              style={{ fontSize: "var(--text-caption)" }}
            >
              Proponha uma nova renda a partir de um coeficiente manual, aplique-a
              ao contrato e notifique o inquilino.
            </p>

            {proposalError && (
              <div
                className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
                style={{ borderRadius: "var(--radius)" }}
              >
                {proposalError}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="coefficient" className={labelClass} style={labelStyle}>
                  Coeficiente (%)
                </label>
                <input
                  id="coefficient"
                  type="number"
                  step="0.01"
                  placeholder="Ex.: 2.16"
                  value={coefficient}
                  onChange={(e) => setCoefficient(e.target.value)}
                  className={inputClass}
                  style={inputStyle}
                  disabled={isLoading}
                />
              </div>
              <div>
                <label htmlFor="effectiveDate" className={labelClass} style={labelStyle}>
                  Data de efeito
                </label>
                <input
                  id="effectiveDate"
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  className={inputClass}
                  style={inputStyle}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="proposalNotes" className={labelClass} style={labelStyle}>
                Notas
              </label>
              <input
                id="proposalNotes"
                type="text"
                placeholder="Opcional"
                value={proposalNotes}
                onChange={(e) => setProposalNotes(e.target.value)}
                className={inputClass}
                style={inputStyle}
                disabled={isLoading}
              />
            </div>

            <div
              className="border border-border bg-muted p-3 flex items-center justify-between gap-3 flex-wrap text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              <span className="text-secondary-text">Renda proposta</span>
              <span
                className="text-foreground"
                style={{ fontWeight: "var(--font-weight-semi-bold)" }}
              >
                {formatCurrency(contractRent)}
                {" → "}
                {previewRent !== null ? formatCurrency(previewRent) : "—"}
              </span>
            </div>

            <button
              type="button"
              onClick={handleCreateProposal}
              disabled={isLoading || isCreatingProposal}
              className="px-4 py-2 min-h-[44px] bg-primary hover:bg-primary/90 text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderRadius: "var(--radius-button)" }}
            >
              {isCreatingProposal ? "A criar…" : "Criar proposta"}
            </button>

            {/* Lista de propostas existentes */}
            <div className="space-y-3">
              {isLoadingProposals ? (
                <p className="text-sm text-secondary-text">A carregar propostas…</p>
              ) : proposals.length === 0 ? (
                <p className="text-sm text-secondary-text">
                  Sem propostas de atualização para este contrato.
                </p>
              ) : (
                proposals.map((proposal) => {
                  const isBusy = proposalBusyId === proposal.id;
                  return (
                    <div
                      key={proposal.id}
                      className="border border-border p-4 space-y-3"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <p style={{ fontWeight: "var(--font-weight-medium)" }}>
                            {formatCurrency(proposal.currentRent)}
                            {" → "}
                            {formatCurrency(proposal.proposedRent)}
                            <span className="text-secondary-text">
                              {" "}
                              ({proposal.coefficientPercent}%)
                            </span>
                          </p>
                          <p
                            className="text-secondary-text mt-0.5"
                            style={{ fontSize: "var(--text-caption)" }}
                          >
                            Efeito a {formatDateShort(proposal.effectiveDate)}
                          </p>
                          {proposal.notes && (
                            <p
                              className="text-secondary-text mt-1 whitespace-pre-wrap"
                              style={{ fontSize: "var(--text-caption)" }}
                            >
                              {proposal.notes}
                            </p>
                          )}
                        </div>
                        <span
                          className={`inline-flex items-center px-2 py-1 text-xs ${proposalStatusBadgeClass(
                            proposal.status,
                          )}`}
                          style={{ borderRadius: "var(--radius)" }}
                        >
                          {proposalStatusLabel(proposal.status)}
                        </span>
                      </div>

                      <div className="flex items-center gap-3 flex-wrap">
                        {proposal.status === "proposta" &&
                          (confirmApplyId === proposal.id ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-secondary-text">
                                Aplicar ao contrato?
                              </span>
                              <button
                                type="button"
                                disabled={isBusy}
                                onClick={() => handleApplyProposal(proposal)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-primary/10 text-primary cursor-pointer disabled:opacity-50"
                                style={{ borderRadius: "var(--radius)" }}
                              >
                                {isBusy ? "A aplicar…" : "Confirmar"}
                              </button>
                              <button
                                type="button"
                                disabled={isBusy}
                                onClick={() => setConfirmApplyId(null)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-border text-foreground hover:bg-accent/50 cursor-pointer disabled:opacity-50"
                                style={{ borderRadius: "var(--radius)" }}
                              >
                                Cancelar
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => setConfirmApplyId(proposal.id)}
                              className="inline-flex items-center gap-1 text-primary text-sm cursor-pointer disabled:opacity-50"
                            >
                              Aplicar
                            </button>
                          ))}

                        {proposal.notifiedByEmail ? (
                          <span className="inline-flex items-center gap-1 text-green-700 text-sm">
                            <CheckmarkCircle20Regular className="w-4 h-4" />
                            Notificado
                          </span>
                        ) : (
                          <button
                            type="button"
                            disabled={isBusy}
                            onClick={() => handleNotifyProposal(proposal)}
                            className="inline-flex items-center gap-1 text-primary text-sm cursor-pointer disabled:opacity-50"
                          >
                            <Mail20Regular className="w-4 h-4" />
                            Notificar
                          </button>
                        )}

                        {confirmDeleteProposalId === proposal.id ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-secondary-text">Apagar?</span>
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => handleDeleteProposal(proposal)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-destructive/10 text-destructive cursor-pointer disabled:opacity-50"
                              style={{ borderRadius: "var(--radius)" }}
                            >
                              Confirmar
                            </button>
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => setConfirmDeleteProposalId(null)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-border text-foreground hover:bg-accent/50 cursor-pointer disabled:opacity-50"
                              style={{ borderRadius: "var(--radius)" }}
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            disabled={isBusy}
                            onClick={() => setConfirmDeleteProposalId(proposal.id)}
                            className="inline-flex items-center gap-1 text-destructive text-sm cursor-pointer disabled:opacity-50"
                          >
                            <Delete20Regular className="w-4 h-4" />
                            Apagar
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="mb-20" />
        </div>
      </div>
    </div>
  );
}
